import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Search, Loader2, Paperclip } from "lucide-react";
import { toast } from "sonner";

const TYPE_COLOR = {
  Customer: "bg-blue-50 text-blue-600",
  Lead: "bg-emerald-50 text-emerald-600",
  Quotation: "bg-violet-50 text-violet-600",
  "Sales Order": "bg-orange-50 text-orange-600",
  "Purchase Order": "bg-teal-50 text-teal-600",
  Invoice: "bg-rose-50 text-rose-600",
};

export function GlobalSearch() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        inputRef.current?.focus();
      }
      if (e.key === "Escape") setOpen(false);
    };
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onClick);
    return () => { window.removeEventListener("keydown", onKey); window.removeEventListener("mousedown", onClick); };
  }, []);

  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); setOpen(false); return; }
    setLoading(true);
    const t = setTimeout(() => {
      api.get("/search", { params: { q } })
        .then((r) => { setResults(r.data.results || []); setOpen(true); })
        .catch(() => {})
        .finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const pick = (r) => {
    setOpen(false);
    setQ("");
    navigate(`${r.to}?q=${encodeURIComponent(r.q || r.title || "")}`);
  };

  const openPoFile = async (e, fid) => {
    e.stopPropagation();
    try {
      const r = await api.get(`/files/${fid}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      toast.error("Could not open the PO file");
    }
  };

  return (
    <div className="relative hidden lg:block w-64 xl:w-72" ref={boxRef}>
      <div className="relative">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        <input
          ref={inputRef}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => { if (results.length) setOpen(true); }}
          placeholder="Search customers, invoices, leads…"
          className="w-full h-10 pl-9 pr-14 text-sm rounded-full bg-slate-100/80 border border-transparent focus:bg-white focus:border-[#3B82F6]/40 focus:ring-2 focus:ring-[#3B82F6]/15 outline-none placeholder:text-slate-400 transition-all duration-200"
          data-testid="global-search-input"
        />
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-semibold text-slate-400 bg-white border border-slate-200 rounded px-1.5 py-0.5 pointer-events-none">
          {loading ? <Loader2 size={10} className="animate-spin" /> : "Ctrl K"}
        </span>
      </div>
      {open && (
        <div className="absolute top-12 left-0 right-0 bg-white rounded-xl border border-slate-100 shadow-[0_16px_48px_rgba(15,23,42,0.14)] overflow-hidden z-50" data-testid="global-search-results">
          <div className="max-h-80 overflow-y-auto scroll-thin py-1">
            {results.map((r) => (
              <div key={`${r.type}-${r.id}`} onClick={() => pick(r)} role="button" tabIndex={0}
                   onKeyDown={(e) => e.key === "Enter" && pick(r)}
                   className="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-slate-50 transition-colors duration-150 cursor-pointer"
                   data-testid={`search-result-${r.id}`}>
                <span className={`text-[9px] font-bold uppercase tracking-wider rounded px-1.5 py-0.5 flex-none ${TYPE_COLOR[r.type] || "bg-slate-100 text-slate-600"}`}>{r.type}</span>
                <span className="min-w-0 flex-1">
                  <span className="block text-[13px] font-medium text-slate-800 truncate">{r.title}</span>
                  {r.sub && <span className="block text-[11px] text-slate-400 truncate">{r.sub}</span>}
                </span>
                {r.po_file_id && (
                  <button onClick={(e) => openPoFile(e, r.po_file_id)} title="View uploaded PO PDF"
                          className="flex-none inline-flex items-center gap-1 text-[10px] font-semibold text-[#3B82F6] bg-[#3B82F6]/10 hover:bg-[#3B82F6]/20 rounded-full px-2 py-1 transition-colors duration-150"
                          data-testid={`search-po-file-${r.id}`}>
                    <Paperclip size={10} /> PO PDF
                  </button>
                )}
              </div>
            ))}
            {results.length === 0 && <div className="text-xs text-slate-400 text-center py-5">No matches found</div>}
          </div>
        </div>
      )}
    </div>
  );
}
